const escapePredictiveSearchRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const filterPredictiveSearchProducts = (root, searchTerm, intentApi = window.RareTCGSearchIntent) => {
  const rows = [...root.querySelectorAll('[data-rtcg-predictive-product]')];
  if (!intentApi || rows.length === 0) return { kept: rows.length, removed: 0 };

  const intent = intentApi.getIntent(searchTerm);
  const keptRows = rows.filter((row) => {
    if (intentApi.matchesProduct(intent, row.dataset)) return true;
    row.remove();
    return false;
  });

  if (keptRows.length === 0) root.querySelector('[data-rtcg-predictive-products-group]')?.remove();
  const removed = rows.length - keptRows.length;
  const liveCount = root.querySelector('[data-predictive-search-live-region-count-value]');
  const countMatch = liveCount?.textContent.match(/\d+/);
  if (removed > 0 && countMatch) {
    const updatedCount = Math.max(0, Number(countMatch[0]) - removed);
    liveCount.textContent = updatedCount === 0
      ? (liveCount.dataset.noResultsText || 'No search results')
      : liveCount.textContent.replace(countMatch[0], String(updatedCount));
  }

  return { kept: keptRows.length, removed };
};

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    if (this.rtcgMobileSuspended) return;
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[role="option"][aria-selected="true"]')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    if (this.rtcgMobileRestoringFocus) return;
    this.rtcgMobileSuspended = false;
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      // Desktop/header predictive search is a transient dropdown and should
      // close when focus leaves. The dedicated RareTCG mobile Search layer is
      // a destination, not a dropdown: dismissing the iOS keyboard must never
      // erase the visible results for the current query.
      if (this.dataset?.rtcgMobileOwned === 'true') {
        if (this.getQuery().length && this.getAttribute('results') === 'true') {
          this.setAttribute('open', true);
          this.input.setAttribute('aria-expanded', true);
          this.isOpen = true;
          this.syncBodyState();
        }
        return;
      }
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (event.code === 'Escape' && (this.rtcgMobileSuspended || this.dataset?.rtcgMobileOwned === 'true')) return;
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      case 'Escape':
        this.close();
        this.input.focus({ preventScroll: true });
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      const previousTermMatches = currentButtonText.match(new RegExp(escapePredictiveSearchRegExp(previousTerm), 'g')) || [];
      if (previousTermMatches.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('[role="option"]')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[role="option"][aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    if (this.rtcgMobileSuspended) return;
    this.rtcgMobileStatus?.('loading');
    const queryKey = `raretcg-${encodeURIComponent(searchTerm.toLowerCase())}`;
    const requestGeneration = (this.requestGeneration || 0) + 1;
    this.requestGeneration = requestGeneration;
    this.latestSearchTerm = searchTerm;
    this.setLiveRegionLoadingState();

    this.abortController.abort();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.abortController = new AbortController();

    const url = `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product,collection&resources[limit]=10&resources[limit_scope]=each&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor&section_id=predictive-search`;

    return fetch(url, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (requestGeneration !== this.requestGeneration || searchTerm !== this.latestSearchTerm) return false;
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
        return true;
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError') {
          // Code 20 means the call was aborted
          return false;
        }
        if (requestGeneration !== this.requestGeneration || this.rtcgMobileSuspended) return false;
        this.close();
        this.rtcgMobileStatus?.('error');
        return false;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (this.rtcgMobileSuspended) return;
    this.rtcgMobileStatus?.('results');
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    filterPredictiveSearchProducts(this.predictiveSearchResults, this.searchTerm);
    // Each new mobile query starts from the top. iOS may preserve/shift the
    // inner scroll position while the keyboard opens, which previously left
    // only the tail (e.g. a lone “COLLECTION” label) visible above Products.
    if (this.dataset?.rtcgMobileOwned === 'true') {
      this.predictiveSearchResults.scrollTop = 0;
      const mobileScroll = this.predictiveSearchResults.querySelector('.rtcg-predictive-results__scroll');
      if (mobileScroll) mobileScroll.scrollTop = 0;
      const contentScroller = this.closest('[data-rtcg-search-layer]')?.querySelector('.rtcg-mobile-layer__content');
      if (contentScroller) contentScroller.scrollTop = 0;
    }
    if (!this.querySelector('#predictive-search-results')) {
      this.close();
      return;
    }
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    if (this.dataset?.rtcgMobileOwned === 'true') return window.innerHeight;
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (this.rtcgMobileSuspended) return;
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    this.syncBodyState();
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      // Abort alone cannot cancel an already-resolved response/body callback.
      this.requestGeneration = (this.requestGeneration || 0) + 1;
      this.abortController.abort();
      this.rtcgMobileStatus?.('idle');
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    this.syncBodyState();
  }

  syncBodyState() {
    requestAnimationFrame(() => {
      const resultsOpen = !!document.querySelector('.header predictive-search[open], .header predictive-search[loading]');
      const discoveryOpen = [...document.querySelectorAll('.header [data-rtcg-search-discovery]')]
        .some((panel) => !panel.hidden);
      document.body?.classList.toggle('rtcg-search-open', resultsOpen || discoveryOpen);
    });
  }
}

customElements.define('predictive-search', PredictiveSearch);
